import React from 'react';
import { X, Download, ExternalLink } from 'lucide-react';

/**
 * Modale d'aperçu PDF.
 * Utilise le viewer PDF natif de Chrome (intégré dans un iframe) pour afficher le rapport.
 *
 * Pourquoi cette approche ?
 * Dans certains environnements (preview Emergent, intranet sandboxé...), l'iframe parent
 * a un attribut `sandbox` qui ne contient pas `allow-downloads`. Tous les téléchargements
 * programmatiques (<a download>, blob+click, window.location.href, etc.) sont alors bloqués
 * par Chrome avec le message "Download is disallowed".
 *
 * En affichant le PDF INLINE (Content-Disposition: inline + ?inline=1), le navigateur
 * utilise son propre viewer PDF, dont la barre d'outils contient un bouton "Télécharger"
 * géré par Chrome lui-même : ce clic n'est PAS soumis au sandbox de la page, et le download
 * fonctionne dans tous les contextes.
 *
 * Props :
 *  - open: bool
 *  - onClose: () => void
 *  - url: URL backend du PDF (sans `?inline=1`, ajouté automatiquement)
 *  - title: titre affiché en haut de la modale
 *  - filename: nom suggéré (utilisé pour le lien "Télécharger" de secours)
 */
export default function PdfPreviewModal({ open, onClose, url, title = 'Aperçu du PDF', filename }) {
  if (!open || !url) return null;

  const sep = url.includes('?') ? '&' : '?';
  const inlineUrl = `${url}${sep}inline=1`;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pdf-preview-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
          <h3 className="text-lg font-semibold text-[#0F172A]">{title}</h3>
          <div className="flex items-center gap-2">
            {/* Lien de téléchargement direct (Chrome viewer a aussi son propre bouton) */}
            <a
              href={url}
              target="_top"
              rel="noopener noreferrer"
              download={filename}
              className="flex items-center gap-2 text-sm text-[#397DFF] hover:bg-[#EFF6FF] px-3 py-1.5 rounded no-underline"
              data-testid="pdf-modal-download-btn"
              title="Télécharger directement"
            >
              <Download className="w-4 h-4" /> Télécharger
            </a>
            <a
              href={inlineUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#64748B] hover:bg-[#F1F5F9] px-3 py-1.5 rounded no-underline"
              data-testid="pdf-modal-open-tab-btn"
              title="Ouvrir dans un nouvel onglet"
            >
              <ExternalLink className="w-4 h-4" /> Nouvel onglet
            </a>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#F1F5F9] rounded transition-colors"
              aria-label="Fermer"
              data-testid="pdf-modal-close-btn"
            >
              <X className="w-5 h-5 text-[#64748B]" />
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 bg-[#F1F5F9]">
          <iframe
            src={inlineUrl}
            title="Aperçu PDF"
            className="w-full h-full border-0"
            data-testid="pdf-modal-iframe"
          />
        </div>

        {/* Footer help text */}
        <div className="px-5 py-2 border-t border-[#E2E8F0] bg-[#F8FAFC] text-xs text-[#64748B]">
          💡 Pour télécharger : cliquez sur l'icône <Download className="w-3 h-3 inline mx-0.5" /> dans la barre du viewer Chrome
          (en haut à droite de l'aperçu) ou utilisez le bouton "Télécharger" ci-dessus.
        </div>
      </div>
    </div>
  );
}
